import { DataFacade } from "../DataFacade";
import { observe, observable, action, Lambda, extendObservable, remove } from "mobx";
import { EventPushResponse, entities, EventPayload } from "sbtech-sports-data-api-client";
import { DeviceDetect, DeviceType } from "sb-resp-lib"
import * as Models from "./Models";
import * as Parsers from "./Parsers";
import { DateTimeFormatter, getCorrectAmericanOddsString } from "../Helpers";
import { MarketFilterController } from "./Parsers";
import { Dictionary, IPersonalizationMethodResponse, EventsObject, MarketsObject, SelectionsObject, SportsObject, ExtDataObject } from "../Interfaces";

// typed params object needed for addOddToSlip method of BetSlipUtil

export type personalizationEvent =
{
    eventSorts: Dictionary<number>,
    sportSortId: number
}

export type AddOddDataType = {
    masterEventID: number;
    eventID: number,
    lineID: number,
    odds: number | string,
    lineGroupID: number,
    lineTypeID: number,
    rowTypeID: number,
    points: number,
    isLive: boolean,
    isQA: boolean,
    eventTypeId: number,
    leagueId: number,
    betId: number,
    preventUpdating: boolean,
    isFavedTeam: boolean
}

export type DataParserDataComposite = {
    dataFacade: DataFacade,
    dateTimeFormatter: DateTimeFormatter,
    deviceDetect: DeviceDetect,
    marketFilterController: MarketFilterController
}

export enum MainMarketsMapping {
    ML = 0,
    HC = 1,
    OU = 2
}

// typed params object needed for OpenGame method of navHandler

export type OpenGameDataType =
    {
        event: any,
        id: number,
        isLive: boolean,
        evTypeForGameBlock: number,
        shouldHideSidePanels: boolean,
        branchID: number;
    }

/*
    The class has 'istanbul ignore' flag , because of testing typescript interface with Jest.
    It`s known issue : (https://github.com/kulshekhar/ts-jest/issues/378)
    The class is fully tested, but propery "events" implement interface "EventsObject" which is not
    testable with Jest - there is no types like in typescript
 */

/* istanbul ignore next */
export class DataParser {

    isInitSports: boolean;
    deviceDetect: DeviceDetect;
    dateTimeFormatter: DateTimeFormatter;
    disposeDataCollection: Lambda;
    disposeShouldRemoveEvents: Lambda;
    disposeTimeZone: Lambda;
    disposeOddStyleChange: Lambda;
    disposeDeviceTypeChange: Lambda;
    disposeDataPersonalizationChange: Lambda;
    disposeExtendedData: Lambda;
    dataFacade: DataFacade;
    betSlipUtil: BetSlipUtil;
    navHandler: Application;
    personalizationData: Dictionary<personalizationEvent>;
    @observable events: EventsObject;
    @observable markets: MarketsObject;
    @observable selections: SelectionsObject;
    @observable sports: SportsObject;
    @observable extDataObjects: ExtDataObject;
    marketFilterController: Parsers.MarketFilterController

    constructor(dataParserDataComposite: DataParserDataComposite) {

        this.isInitSports = true;
        this.dataFacade = dataParserDataComposite.dataFacade;
        this.events = {};
        this.markets = {};
        this.selections = {};
        this.sports = {};
        this.extDataObjects = {};
        this.personalizationData = {};
        this.marketFilterController = dataParserDataComposite.marketFilterController;

        this.betSlipUtil = dataParserDataComposite.dataFacade.betSlipUtil;
        this.navHandler = dataParserDataComposite.dataFacade.navHandler;
        this.deviceDetect = dataParserDataComposite.deviceDetect;
        this.dateTimeFormatter = dataParserDataComposite.dateTimeFormatter;
        this.disposeDataCollection = () => {};
        this.disposeShouldRemoveEvents = () => {};
        this.disposeTimeZone = () => {};
        this.disposeOddStyleChange = () => {};
        this.disposeDeviceTypeChange = () => {};
        this.disposeDataPersonalizationChange = () => {};
        this.disposeExtendedData = () => {};

    }

    activate() {
        this.disposeDataCollection = observe(this.dataFacade, "dataCollection", (change) => {
            this.updateCollection(change.newValue as EventPushResponse);
        });

        this.disposeShouldRemoveEvents = observe(this.dataFacade, "shouldRemoveEvents", (change) => {
            (change.newValue === true) && this.dataFacade.onRefreshSportContent();
        });

        this.disposeTimeZone = observe(this.dataFacade, "currentTimeZone", (change) => {
            this.updateEventsDate(change.newValue, this.deviceDetect.deviceTypeValue);
        });

        this.disposeOddStyleChange = observe(this.dataFacade, "currentOddStyle", (change) => {
            this.updateEventsOddStyle(change.newValue);
        });

        this.disposeDataPersonalizationChange = observe(this.dataFacade, "personalizationDataResponse", (change) => {
            this.isInitSports = true;
            this.updatePersonalizatedData(change.newValue);
        });

        this.disposeDeviceTypeChange = observe(this.deviceDetect, "deviceTypeValue", (change) => {
            this.updateEventsDate(this.dataFacade.currentTimeZone, change.newValue);
            this.updateSelectionsDisplayName(change.newValue);
        });

        this.disposeExtendedData = observe(this.dataFacade, "eventsExtendedData", (change) => {
            this.updateEventsExtendedData(change.newValue);
        });
        this.dataFacade.activate();
    }

    deactivate() {
        this.disposeTimeZone();
        this.disposeOddStyleChange();
        this.disposeDataCollection();
        this.disposeShouldRemoveEvents();
        this.disposeDataPersonalizationChange();
        this.disposeDeviceTypeChange();
        this.disposeExtendedData();
        this.dataFacade.deactivate();
    }

    @action.bound updatePersonalizatedData(PersonalizationMethodResponse: IPersonalizationMethodResponse): void {
        this.personalizationData = {};

        let sortOrder = 0;
        PersonalizationMethodResponse.BranchGroups.forEach(sport => {
            let events = {};
            sport.Events.forEach(event => {
                events[event.Id] = event.SortId
            });

            let personalizationEvent: personalizationEvent = {
                eventSorts: events,
                sportSortId: sortOrder++,
            }

            this.personalizationData[sport.Id] = personalizationEvent;
        });
    }

    @action updateCollection(dataCollection: EventPushResponse): void {

        dataCollection.data && Object.keys(dataCollection.data).forEach((payload)=>{
            this[payload](dataCollection.data[payload]);
        });
    }

    @action.bound add(payload: EventPayload<entities.SportEvent, entities.Market>){
        !DataParser.isEmpty(payload.markets) && this.addMarketsSelections(payload.markets);
        !DataParser.isEmpty(payload.events) && this.addEvents(payload.events);
        this.initSports();
        !DataParser.isEmpty(payload.events) && !this.isInitSports && this.addEventIdsToSport(payload.events);
    }

    @action.bound change(payload: EventPayload<entities.SportEventChange, entities.MarketChange>) {
        !DataParser.isEmpty(payload.markets) && this.updateMarketsSelections(payload.markets);
        !DataParser.isEmpty(payload.events) && this.updateEvents(payload.events);
    }

    @action.bound remove(payload: EventPayload<string, string>) {
        !DataParser.isEmpty(payload.markets) && this.removeMarketsSelections(payload.markets);
        !DataParser.isEmpty(payload.events) && this.removeEvents(payload.events);
        !DataParser.isEmpty(payload.events) && this.dataFacade.onRefreshSportContent();
    }

    @action initSports() {

        if(!this.isInitSports){
            return;
        }

        this.sports = Parsers.SportParser.getSports(
            this.personalizationData,
            this.events,
            this.dataFacade.cms.IsEnableForYoutab,
            this.dataFacade.dict)

        if (this.dataFacade.cms.IsEnableForYoutab) {
            this.processPersonalizedSports();
        }

        this.isInitSports = false;
    }

    @action.bound processPersonalizedSports(): void {
        for (var eventId in this.personalizationData[0].eventSorts) {
            if (this.personalizationData[0].eventSorts.hasOwnProperty(eventId)) {
                let event = this.events[eventId];
                event && (event.personalizedSortId = this.personalizationData[0].eventSorts[eventId].toString());
            }
        }
    }

    addEventIdsToSport(events) {
        events.forEach(event => {
            !this.sports[event.sportId].eventIds.find(function(ev) { return ev === event.id}) && this.sports[event.sportId].addEventId(event.id);
        });
    }

    @action addMarketsSelections(markets: entities.Market[]): void {

        for (let index = 0; index < markets.length; index++) {
            let market = markets[index];

            if (!this.marketFilterController.isValid(market) || typeof this.markets[market.id] !== "undefined") {
                continue;
            }

            let parsedMarket = Parsers.parseMarket(market);
            extendObservable(this.markets, {
                [parsedMarket.id]: parsedMarket
            })

            market.selections.forEach((selection)=>{
                this.addSelection(market, selection);
            })
        }
    }

    addSelection(market:entities.Market, selection:entities.Selection) {

            if (typeof (this.selections[selection.id]) !== "undefined") {
                this.selections[selection.id].setOdds(selection.displayOdds[this.dataFacade.currentOddStyle], selection.displayOdds);
                return;
            }

            let displayName = Parsers.getSelectionDisplayName(
                selection.name,
                selection.outcomeType ? selection.outcomeType.toString() : "",
                market.marketType.id,
                this.deviceDetect.deviceTypeValue,
                { Draw: this.dataFacade.dict.TabletMobileDraw });

            let parsedSelection = Parsers.parseSelection(selection, market, this.dataFacade.currentOddStyle, displayName);

            extendObservable(this.selections, {
                [parsedSelection.id]: parsedSelection
            })
    }

    @action addEvents(newEvents: entities.SportEvent[]) {
        for (let index = 0; index < newEvents.length; index++) {
            let newEvent = newEvents[index];

            if(typeof this.events[newEvent.id] !== "undefined") { continue; }

            let parsedEvent = Parsers.EventParser.parse(newEvent, this.dataFacade.currentTimeZone,
                this.personalizationData[newEvent.sportId].eventSorts[newEvent.id].toString(),
                Parsers.EventParser.getFilteredMarketIds(newEvent, this.markets),
                );

            this.updateFormattedDate(parsedEvent, this.dataFacade.currentTimeZone, this.deviceDetect.deviceTypeValue);

            extendObservable(this.events, {
                [parsedEvent.id]: parsedEvent
            })
        }
    }

    @action updateEvents(updatedEvents: entities.SportEventChange[]): void {

        for (let index = 0; index < updatedEvents.length; index++) {
            let updateEvent = updatedEvents[index];
            let existingEvent = this.events[updateEvent.id];

            if (existingEvent) {
                let updatedMarketIds = Parsers.EventParser.getFilteredMarketIds(updateEvent, this.markets);
                existingEvent.updateMarkets(updatedMarketIds, updateEvent.marketLinesCount);
                this.updateFormattedDate(existingEvent, this.dataFacade.currentTimeZone, this.deviceDetect.deviceTypeValue);
            }
        }

    }

    @action updateEventsExtendedData(newData)
    {
        for (let i = 0; i < newData.length; i += 1)
        {
            let parsedExtendedData = Parsers.ExtendedDataParser.parse(newData[i], this.dataFacade.statisticsProvider);
            this.extDataObjects[parsedExtendedData.eventId] = parsedExtendedData;
        }
    }

    updateEventsOddStyle(newOddStyle: string) {
        for (let selection in this.selections) {
            if (!this.selections.hasOwnProperty(selection)) { continue; }
            let currentSelection = this.selections[selection];
            currentSelection.setOddStyleChange(newOddStyle);
        }
    }

    updateEventsDate(newTime: number, deviceType: DeviceType) {
        for (let event in this.events) {
            if (!this.events.hasOwnProperty(event)) { continue; }
            let ev = this.events[event];
            this.updateFormattedDate(ev, newTime, deviceType);
        }
    }

    updateSelectionsDisplayName(deviceType: DeviceType) {
        for (let selectionId in this.selections) {
            if (!this.selections.hasOwnProperty(selectionId)) { continue; }
            let selection = this.selections[selectionId];
            let displayName = Parsers.getSelectionDisplayName(
                selection.name,
                selection.outcomeType,
                this.markets[selection.marketId].typeId,
                this.deviceDetect.deviceTypeValue,
                { Draw: this.dataFacade.dict.TabletMobileDraw });

            selection.setDisplayName(displayName);
        }
    }

    @action updateFormattedDate(ev: Models.Event, newTimeZone: number, deviceType: DeviceType): void {
        let dateFormatted = this.dateTimeFormatter.adjustDate(ev.eventDate, newTimeZone, deviceType);
        ev.updateDateTime(dateFormatted.date, dateFormatted.time, ev.eventDate);
    }

    @action removeMarketsSelections(removedMarkets: string[]) {

        for (let index = 0; index < removedMarkets.length; index++) {
            let removedMarket = this.markets[removedMarkets[index]];

            if (removedMarket) {
                for (let i = 0; i < removedMarket.selectionIds.length; i++) {
                    remove(this.selections, removedMarket.selectionIds[i]);
                }
                remove(this.markets, removedMarket.id);
            }
        }
    }

    @action removeEventIdFromSport(sportId, evId) {
        this.sports["0"] && (this.sports["0"].eventIds = this.sports["0"].eventIds.filter((eventId)=>eventId !== evId));
        this.sports[sportId].eventIds = this.sports[sportId].eventIds.filter((eventId)=>eventId !== evId);
    }

    @action removeEvents(removedEvents: string[]): void {
        for (let index = 0; index < removedEvents.length; index++) {
            let removedEvent = this.events[removedEvents[index]];
            this.removeEventIdFromSport(this.events[removedEvent.id].sportId, removedEvent.id);
            remove(this.events,removedEvent.id);
        }
    }

    addToSlipTranspiler(selectionId) {
        if (!this.selections[selectionId] ||
            !this.events[this.markets[this.selections[selectionId].marketId].eventId]
        ) { return; }
        let selection = this.selections[selectionId];
        let market = this.markets[selection.marketId];
        let event = this.events[market.eventId];

        let addOddData: AddOddDataType = {
            masterEventID: parseInt(event.id),
            eventID: parseInt(this.getMarketId(selection.marketId)),
            lineID: parseInt(selection.sbTechId),
            odds: parseInt(getCorrectAmericanOddsString(selection.displayOdds)),
            lineGroupID: parseInt(selection.lineGroupId),
            lineTypeID: parseInt(selection.type),
            rowTypeID: selection.getSide(),
            points: selection.points,
            isLive: false,
            isQA: false,
            eventTypeId: parseInt(this.getMarketTypeId(selection.marketId)),
            leagueId: parseInt(event.leagueId),
            betId: 0,
            preventUpdating: false,
            isFavedTeam: false
        };

        this.betSlipUtil.addOddToSlip(
            addOddData.masterEventID,
            addOddData.eventID,
            addOddData.lineID,
            addOddData.odds,
            addOddData.lineGroupID,
            addOddData.lineTypeID,
            addOddData.rowTypeID,
            addOddData.points,
            addOddData.isLive,
            addOddData.isQA,
            addOddData.eventTypeId,
            addOddData.leagueId,
            addOddData.betId,
            addOddData.preventUpdating,
            addOddData.isFavedTeam
        );

    }

    openGameTranspiler(ev: React.MouseEvent<HTMLAnchorElement>, eventId) {
        if (!eventId || !this.events[eventId]) { return; }

        let event = this.events[eventId];

        let openGameData: OpenGameDataType =
            {
                event: ev,
                id: parseInt(event.id),
                isLive: false,
                evTypeForGameBlock: 0,
                shouldHideSidePanels: false,
                branchID: parseInt(event.sportId)
            }

        this.navHandler.OpenGame(
            openGameData.event,
            openGameData.id,
            openGameData.isLive,
            openGameData.evTypeForGameBlock,
            openGameData.shouldHideSidePanels,
            openGameData.branchID
        )
    }

    openStatTranspiler(ev: Event, eventId: string) {
        let statObject = this.extDataObjects[eventId];
        this.dataFacade.statisticsProvider.factory.openStats(statObject.statisticsInfo, ev)
    }

    @action updateMarketsSelections(updatedMarkets: entities.MarketChange[]): void {
        updatedMarkets.forEach((market)=>{
            this.markets[market.id] && this.updateSelections(market.selections);
        });
    }

    @action updateSelections(updatedSelections: entities.SelectionChange[]) {
        updatedSelections.forEach((selection)=>{
            this.selections[selection.id] && this.selections[selection.id].setOdds(selection.displayOdds[this.dataFacade.currentOddStyle], selection.displayOdds);
        });
    }

    getMarketId(marketId: string): string {

        let market = this.markets[marketId];

        if (market && (market.id.charAt(0) === MainMarketsMapping.ML.toString())
            || (market.id.charAt(0) === MainMarketsMapping.HC.toString())
            || (market.id.charAt(0) === MainMarketsMapping.OU.toString())) {
            return market.id.substring(2, market.id.length);
        }

        return market.id;
    }

    getMarketTypeId(marketId: string): string {

        let market = this.markets[marketId];

        if (market && (market.id.charAt(0) === MainMarketsMapping.ML.toString())
            || (market.id.charAt(0) === MainMarketsMapping.HC.toString())
            || (market.id.charAt(0) === MainMarketsMapping.OU.toString())) {
            return market.typeId.substring(2, market.typeId.length);
        }

        return market.typeId;
    }

    static isEmpty(collection : any): boolean
    {
        return !!(collection && collection.length <= 0);
    }

}